import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GameVariant } from '@draughts/shared';
import {
  computeGameRulesMeta,
  computeRulesMeta,
  computeTournamentRulesMeta,
  computePageMeta,
  type PageMeta,
} from './meta';
import { isRulesLang, type ContentPage } from './routes';

/** Upsert a <meta name=...> / <meta property=...> tag. */
function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Replace the canonical and the hreflang set.
 *
 * Toglie TUTTI i link di quei due tipi, non solo quelli che ha messo lui: la
 * pagina pre-renderizzata ne porta gia' nove, scritti nell'HTML e quindi senza
 * il marcatore: prima che questa riga esistesse, al montaggio se ne aggiungevano
 * altri nove sopra, e la pagina dichiarava due canonical e diciotto hreflang.
 */
function setManagedLinks(links: { rel: string; href: string; hreflang?: string }[]) {
  document.head
    .querySelectorAll('link[data-page-head], link[rel="canonical"], link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
  for (const l of links) {
    const el = document.createElement('link');
    el.setAttribute('data-page-head', '');
    el.rel = l.rel;
    el.href = l.href;
    if (l.hreflang) el.hreflang = l.hreflang;
    document.head.appendChild(el);
  }
}

/**
 * Replace the structured data. The prerendered HTML already carries a ld+json
 * block; this one is marked so that navigating in-app REPLACES it instead of
 * stacking a second, contradictory graph on top of the first.
 */
function setJsonLd(data: Record<string, unknown>) {
  document.head.querySelectorAll('script[data-page-head]').forEach((el) => el.remove());
  document.head
    .querySelectorAll('script[type="application/ld+json"]:not([data-page-head])')
    .forEach((el) => el.remove());
  const el = document.createElement('script');
  el.setAttribute('type', 'application/ld+json');
  el.setAttribute('data-page-head', '');
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
}

/** Which page the head should describe. */
export type HeadTarget =
  | { page: 'rules'; variant: GameVariant | null; openings?: boolean; capture?: boolean }
  | { page: 'rules-game' }
  | { page: 'rules-tournaments' }
  | { page: ContentPage; dateModified?: string };

/**
 * Client-side <head> updater: keeps title/description/canonical/hreflang/
 * OpenGraph/JSON-LD in sync during SPA navigation. The prerendered HTML covers
 * first paint and non-JS crawlers; this covers in-app navigation, where nothing
 * else would — before it existed, walking into a page from inside the app left
 * the site-wide English head in place.
 */
export function usePageHead(target: HeadTarget) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  // Objects are rebuilt on every render; the primitives are what actually change.
  const isRules = target.page === 'rules';
  // Un primitivo suo, e non solo un ramo dentro l'effetto: le dipendenze qui
  // sotto sono tutte primitive estratte a mano, e senza questo l'effetto non
  // ri-girerebbe entrando o uscendo dalle regole dei tornei — resterebbe in
  // testa il <head> della pagina precedente.
  const isTournamentRules = target.page === 'rules-tournaments';
  // Stesso discorso, e lo stesso difetto se manca: senza questo primitivo
  // nell'elenco delle dipendenze, entrare nelle regole di gioco dall'indice
  // lascerebbe in testa il canonical dell'indice — `/en/rules` su una pagina
  // che sta a `/en/rules/how-to-play`, invisibile se non in devtools.
  const isGameRules = target.page === 'rules-game';
  const isAnyRules = isRules || isTournamentRules || isGameRules;
  const variant = isRules ? target.variant : null;
  const openings = isRules ? target.openings === true : false;
  const capture = isRules ? target.capture === true : false;
  const contentPage = isAnyRules ? null : (target.page as ContentPage);
  const dateModified = isAnyRules ? undefined : target.dateModified;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!isRulesLang(lang)) return;

    const meta: PageMeta = isTournamentRules
      ? computeTournamentRulesMeta(lang, t)
      : isGameRules
        ? computeGameRulesMeta(lang, t)
        : contentPage
          ? computePageMeta(lang, contentPage, t, dateModified ? { dateModified } : {})
          : computeRulesMeta(lang, variant, t, openings, capture);

    document.title = meta.title;
    upsertMeta('name', 'description', meta.description);
    upsertMeta('property', 'og:title', meta.og.title);
    upsertMeta('property', 'og:description', meta.og.description);
    upsertMeta('property', 'og:url', meta.og.url);
    upsertMeta('property', 'og:type', meta.og.type);
    upsertMeta('property', 'og:locale', meta.og.locale);
    upsertMeta('name', 'twitter:title', meta.og.title);
    upsertMeta('name', 'twitter:description', meta.og.description);

    setManagedLinks([
      { rel: 'canonical', href: meta.canonical },
      ...meta.alternates.map((a) => ({ rel: 'alternate', href: a.href, hreflang: a.hreflang })),
    ]);
    setJsonLd(meta.jsonLd);
  }, [contentPage, dateModified, variant, openings, capture, isTournamentRules, isGameRules, lang, t]);
}
